import React, { createContext, useState, useEffect, useContext } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';

const TenantContext = createContext();

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTenantData();
  }, []);

  const loadTenantData = async () => {
    try {
      // Get user attributes from Cognito
      const attributes = await fetchUserAttributes();
      const tenantID = attributes['custom:tenantID'];
      const userType = attributes['custom:userType'];
      
      if (!tenantID) {
        throw new Error('TenantID not found in Cognito attributes');
      }
      
      const apiUrl = process.env.REACT_APP_TENANT_API_URL || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';
      
      // 🏢 COMPANY USER (1% of users)
      if (userType === 'company') {
        // For company users, tenantID field contains companyID
        const response = await fetch(`${apiUrl}/v2/company/config?companyID=${tenantID}`);
        
        if (!response.ok) {
          throw new Error('Failed to load company data');
        }
        
        const companyData = await response.json();
        
        // Transform company data to look like tenant data for compatibility
        const tenantData = {
          tenantID: companyData.companyID,
          companyName: companyData.companyName,
          cloudAccounts: {
            aws: [],
            organizations: []
          },
          companyAccounts: companyData.accounts || [],
          userType: 'company'
        };
        
        setTenant(tenantData);
        setLoading(false);
        return;
      }
      
      // 🏠 TENANT USER (99% of users) - UNCHANGED FLOW
      const response = await fetch(`${apiUrl}/v2/tenant/settings?tenantID=${tenantID}`);
      
      if (!response.ok) {
        throw new Error('Failed to load tenant data');
      }
      
      const tenantData = await response.json();
      setTenant(tenantData);
      
      // Auto-select first org if only one exists
      if (tenantData.cloudAccounts?.organizations?.length === 1) {
        setSelectedOrg(tenantData.cloudAccounts.organizations[0]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to load tenant:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const refreshTenant = () => {
    setLoading(true);
    loadTenantData();
  };

  const getAllAccounts = () => {
    if (!tenant) return [];
    
    // For company users, return only assigned accounts
    if (tenant.userType === 'company') {
      return (tenant.companyAccounts || []).map(acc => ({
        accountId: acc.accountId,
        accountName: acc.accountName,
        tenantID: acc.tenantID,
        type: 'company'
      }));
    }
    
    // Regular tenant user flow
    if (!tenant?.cloudAccounts) return [];
    
    const accounts = [];
    
    // Flat AWS accounts (v1)
    if (tenant.cloudAccounts.aws && Array.isArray(tenant.cloudAccounts.aws)) {
      accounts.push(...tenant.cloudAccounts.aws.map(acc => ({
        accountId: acc.accountId || acc.iamrole?.split(':')[4],
        accountName: acc.alias || acc.accountId,
        iamRole: acc.iamrole,
        type: 'standalone'
      })));
    }
    
    // Organizations (v2)
    if (tenant.cloudAccounts.organizations && Array.isArray(tenant.cloudAccounts.organizations)) {
      tenant.cloudAccounts.organizations.forEach(org => {
        // Management account
        if (org.managementRoleArn) {
          accounts.push({
            accountId: org.id,
            accountName: `${org.alias} (Management)`,
            iamRole: org.managementRoleArn,
            organizationID: org.orgId,
            type: 'management',
            enabledRegions: org.enabledRegions || []
          });
        }
        
        // Member accounts
        if (org.memberAccounts && Array.isArray(org.memberAccounts)) {
          org.memberAccounts.forEach(member => {
            if (member.connected) {
              accounts.push({
                accountId: member.accountId,
                accountName: member.accountName,
                iamRole: member.memberRoleArn,
                organizationID: org.orgId,
                type: 'member',
                enabledRegions: member.enabledRegions || []
              });
            }
          });
        }
      });
    }
    
    return accounts;
  };

  const inviteUser = async (email, role = 'user') => {
    try {
      const apiUrl = process.env.REACT_APP_TENANT_API_URL || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/v2/users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenantID: tenant?.tenantID,
          email,
          role
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to invite user');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to invite user:', error);
      throw error;
    }
  };

  const getTenantUsers = async () => {
    try {
      const apiUrl = process.env.REACT_APP_TENANT_API_URL || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/v2/users?tenantID=${tenant?.tenantID}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Failed to get tenant users:', error);
      throw error;
    }
  };

  const removeUser = async (email) => {
    try {
      const apiUrl = process.env.REACT_APP_TENANT_API_URL || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/v2/users?tenantID=${tenant?.tenantID}&email=${email}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove user');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to remove user:', error);
      throw error;
    }
  };

  const value = {
    tenant,
    selectedOrg,
    selectedAccount,
    setSelectedOrg,
    setSelectedAccount,
    loading,
    error,
    refreshTenant,
    getAllAccounts,
    // User management functions
    inviteUser,
    getTenantUsers,
    removeUser,
    // Helper getters
    tenantID: tenant?.tenantID,
    organizationID: tenant?.organizationID,
    scanRegions: tenant?.scanRegions?.split(',').map(r => r.trim()) || []
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};
